import {Box, Button, Card, CardActions, CardContent, CardHeader, Divider, TextField, FormControl, Unstable_Grid2 as Grid, MenuItem, Alert, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, Chip } from '@mui/material';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CloudUpload, CheckCircle, Error as ErrorIcon, Download } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import {Layout} from "../layout/Layout";
import {Link, useNavigate, useParams} from "react-router-dom";
import theme from "../../theme/theme";
import {ChangeEvent, useEffect, useState, useRef} from "react";
import {Order} from "../../types/order";
import {OrderForm} from "../../types/orderForm";
import {getCreateForm, orderCreate} from "../../api/orderService";
import { useHeaders } from '../../hooks/useHeaders';
import {clientGetSingle, clientCreate, getCreateForm as getClientCreateForm} from "../../api/clientService";
import {Client} from "../../types/client";
import {ClientForm} from "../../types/clientForm";

interface ImportRow {
    rowNumber: number;
    orderNumber?: string;
    fullName?: string;
    nationalId?: string;
    quantity_per_kg?: number;
    price?: number;
    total?: number;
    orderDate?: Date;
}

interface ImportResult {
    success: boolean;
    row: number;
    data: ImportRow;
    error?: string;
    clientCreated?: boolean;
    orderCreated?: boolean;
}

export const AddOrder = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const storedData = sessionStorage.getItem('auth');
    const sessionAuthData = storedData ? JSON.parse(storedData) : null;
    const user = sessionAuthData ? sessionAuthData.user : null;
    const [order, setOrder] = useState<Order>();
    const [orderCreateForm, setOrderCreateForm] = useState<OrderForm>();
    const [clientForm, setClientForm] = useState<ClientForm>();
    const [errors, setErrors] = useState({
        quantity_per_kg: false,
        price: false,
        orderDate: false,
        type: false,
        totalPrice: false
    });

    // Excel import states
    const [excelFile, setExcelFile] = useState<File | null>(null);
    const [parsedData, setParsedData] = useState<ImportRow[]>([]);
    const [importResults, setImportResults] = useState<ImportResult[]>([]);
    const [importing, setImporting] = useState(false);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [showResultsDialog, setShowResultsDialog] = useState(false);
    const [importErrors, setImportErrors] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const headers = useHeaders();

    useEffect( () => {
        const getOrderCreateForm = async () => {
            try {
                // const orderDate = order?.orderDate ? order?.orderDate : new Date();
                const client = await clientGetSingle(Number(clientId), headers);
                const [orderCreateFormResponse, clientFormResponse] = await Promise.all([
                    getCreateForm(headers),
                    getClientCreateForm(headers)
                ]);

                setOrderCreateForm(orderCreateFormResponse);
                setClientForm(clientFormResponse);
                if (user !== null) {
                    setOrder({...order, idUser: user, idClient: client, type: 'Katalizator mbeturinë'});
                }
            } catch (error) {
                console.error('Failed to fetch forms:', error);
            }
        }

        getOrderCreateForm();
    }, []);

    useEffect(() => {
        const calculateTotalPrice = async () =>{
            setOrder({ ...order,
                totalPrice: Number((Number(order?.quantity_per_kg) * Number(order?.price)).toFixed(2))});
        }

        calculateTotalPrice();
    }, [order?.quantity_per_kg, order?.price]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setOrder({
            ...order,
            [name]: value,
        });

        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: value ? false : true,
        }));
    };

    const onDateChange = (val: any) => {
        setOrder({
            ...order,
            orderDate: val
        });
        setErrors({
            ...errors,
            orderDate: val ? false : true,
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!order?.idClient || !order?.quantity_per_kg || !order?.price || !order?.orderDate ||
            !order?.type){
            setErrors({
                ...errors,
                quantity_per_kg: !order?.quantity_per_kg ? true : false,
                price: !order?.price ? true : false,
                orderDate: !order?.orderDate ? true : false,
                type: !order?.type ? true : false,
                totalPrice: !order?.totalPrice ? true : false
            });
            return;
        }

        try{
            const res = await orderCreate(order, headers);

            if(res){
                navigate(`/clients/${clientId}`);
            }
        }catch(error) {
            console.error('Order create failed', error);
        }
    }

    // Excel import functions
    const handleExcelFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            setExcelFile(selectedFile);
            parseExcelFile(selectedFile);
        }
    };

    const parseExcelDate = (dateValue: any): Date | undefined => {
        if (!dateValue) return undefined;
        if (dateValue instanceof Date) return dateValue;
        if (typeof dateValue === 'number') {
            // Excel stores dates as numbers (days since 1900-01-01)
            const excelDate = new Date((dateValue - 25569) * 86400 * 1000);
            return excelDate;
        }
        if (typeof dateValue === 'string') {
            const parsed = new Date(dateValue);
            return isNaN(parsed.getTime()) ? undefined : parsed;
        }
        return undefined;
    };

    const parseExcelFile = async (file: File) => {
        setImportErrors([]);
        setParsedData([]);

        try {
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

            if (jsonData.length < 2) {
                setImportErrors(['Excel file must have at least one data row']);
                return;
            }

            // Expected headers: ID, Full Name, National ID, Quantity (kg), Price, Total, Order Date
            const expectedHeaders = ['ID', 'Full Name', 'National ID', 'Quantity (kg)', 'Price', 'Total', 'Order Date'];
            const headers = jsonData[0];
            const validationErrors: string[] = [];

            // Validate headers
            expectedHeaders.forEach((expectedHeader, index) => {
                if (headers[index] !== expectedHeader) {
                    validationErrors.push(`Column ${index + 1} should be "${expectedHeader}", but is "${headers[index] || 'empty'}"`);
                }
            });

            if (validationErrors.length > 0) {
                setImportErrors(validationErrors);
                return;
            }

            // Parse data rows
            const parsed: ImportRow[] = [];
            const parseErrors: string[] = [];

            for (let i = 1; i < jsonData.length; i++) {
                const row = jsonData[i];
                const rowNumber = i + 1;

                if (row.every(cell => !cell)) continue; // Skip empty rows

                const importRow: ImportRow = {
                    rowNumber,
                    orderNumber: row[0]?.toString().trim(),
                    fullName: row[1]?.toString().trim(),
                    nationalId: row[2]?.toString().trim(),
                    quantity_per_kg: parseFloat(row[3]) || 0,
                    price: parseFloat(row[4]) || 0,
                    total: parseFloat(row[5]) || 0,
                    orderDate: parseExcelDate(row[6])
                };

                // Validate required fields
                if (!importRow.orderNumber) parseErrors.push(`Row ${rowNumber}: ID is required`);
                if (!importRow.fullName) parseErrors.push(`Row ${rowNumber}: Full Name is required`);
                if (!importRow.nationalId) parseErrors.push(`Row ${rowNumber}: National ID is required`);
                if (!importRow.quantity_per_kg || importRow.quantity_per_kg <= 0) parseErrors.push(`Row ${rowNumber}: Quantity (kg) must be greater than 0`);
                if (!importRow.price || importRow.price <= 0) parseErrors.push(`Row ${rowNumber}: Price must be greater than 0`);
                if (!importRow.orderDate) parseErrors.push(`Row ${rowNumber}: Order Date is required`);

                parsed.push(importRow);
            }

            if (parseErrors.length > 0) {
                setImportErrors(parseErrors);
            } else {
                setParsedData(parsed);
                setShowImportDialog(true);
            }
        } catch (error) {
            setImportErrors(['Error reading Excel file']);
            console.error('Excel parsing error:', error);
        }
    };

    const findExistingClient = (nationalId: string): Client | undefined => {
        if (!orderCreateForm?.clients) return undefined;
        return orderCreateForm.clients.find(client => client.nationalId === nationalId);
    };

    const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
        const parts = fullName.trim().split(' ');
        if (parts.length === 1) {
            return { firstName: parts[0], lastName: '' };
        }
        const firstName = parts[0];
        const lastName = parts.slice(1).join(' ');
        return { firstName, lastName };
    };

    const handleExcelImport = async () => {
        if (!parsedData.length || !orderCreateForm || !clientForm) return;

        setImporting(true);
        const results: ImportResult[] = [];

        for (const item of parsedData) {
            let clientCreated = false;
            let orderCreated = false;
            let client: Client | undefined;

            try {
                // Check if client already exists
                client = findExistingClient(item.nationalId!);

                if (!client) {
                    // Create new client
                    const { firstName, lastName } = splitFullName(item.fullName!);
                    
                    // Use the first available country from the form data
                    const defaultCountry = clientForm.countries && clientForm.countries.length > 0 
                        ? clientForm.countries[0] 
                        : undefined;
                    
                    if (!defaultCountry) {
                        results.push({
                            success: false,
                            row: item.rowNumber,
                            data: item,
                            error: 'Nuk ka vende të disponueshme në sistem',
                            clientCreated: false,
                            orderCreated: false
                        });
                        continue;
                    }
                    
                    const clientData: Client = {
                        firstName,
                        lastName,
                        nationalId: item.nationalId,
                        idCountry: defaultCountry, // Use the first available country
                        idUser: user,
                        isImported: true, // Always set to true for imported clients
                        // Set other fields to undefined as specified
                        email: undefined,
                        phone: undefined,
                        address: undefined,
                        place: undefined,
                        digitalSignature: null,
                        idMunicipality: undefined
                    };

                    const clientResult = await clientCreate(clientData, headers);
                    
                    // Extract the client object from the API response
                    client = clientResult.client;
                    clientCreated = !clientResult.isExisting; // True if new, false if existing
                    
                    if (!client || !client.idClient) {
                        results.push({
                            success: false,
                            row: item.rowNumber,
                            data: item,
                            error: 'Client could not be created or found',
                            clientCreated: false,
                            orderCreated: false
                        });
                        continue;
                    }
                }

                // Create order for the client
                if (!client || !client.idClient) {
                    results.push({
                        success: false,
                        row: item.rowNumber,
                        data: item,
                        error: 'Klienti nuk ka ID të vlefshëm',
                        clientCreated,
                        orderCreated: false
                    });
                    continue;
                }
                
                const orderNumber = item.orderNumber ? parseInt(item.orderNumber, 10) : undefined;
                const orderData: Order = {
                    idClient: client,
                    orderNumber: orderNumber !== undefined && !isNaN(orderNumber) ? orderNumber : undefined,
                    quantity_per_kg: item.quantity_per_kg,
                    price: item.price,
                    totalPrice: item.total,
                    orderDate: item.orderDate, // Use date from Excel
                    type: 'Katalizator mbeturinë', // Default type
                    idUser: user
                };

                await orderCreate(orderData, headers);
                orderCreated = true;

                results.push({
                    success: true,
                    row: item.rowNumber,
                    data: item,
                    clientCreated,
                    orderCreated
                });

            } catch (error) {
                results.push({
                    success: false,
                    row: item.rowNumber,
                    data: item,
                    error: error instanceof Error ? error.message : 'Unknown error',
                    clientCreated,
                    orderCreated: false
                });
            }
        }

        setImportResults(results);
        setImporting(false);
        setShowImportDialog(false);
        setShowResultsDialog(true);
    };

    const downloadTemplate = () => {
        const template = [
            ['ID', 'Full Name', 'National ID', 'Quantity (kg)', 'Price', 'Total', 'Order Date'],
            ['ORD-001', 'John Doe', '1234567890', '10', '5.50', '55.00', '2024-01-15'],
            ['ORD-002', 'Jane Smith', '0987654321', '5', '10.00', '50.00', '2024-01-16'],
            ['', '', '', '', '', '', ''],
            ['NOTE: Delete example rows and replace with your data', '', '', '', '', '', ''],
            ['NOTE: ID maps to orderNumber in the system', '', '', '', '', '', ''],
            ['NOTE: Full Name will be split into firstName and lastName', '', '', '', '', '', ''],
            ['NOTE: Order Date format: YYYY-MM-DD', '', '', '', '', '', '']
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(template);
        worksheet['!cols'] = [
            { width: 15 }, // ID
            { width: 20 }, // Full Name
            { width: 15 }, // National ID
            { width: 14 }, // Quantity (kg)
            { width: 12 }, // Price
            { width: 12 }, // Total
            { width: 15 }  // Order Date
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
        XLSX.writeFile(workbook, 'order_import_template.xlsx');
    };

    const resetImport = () => {
        setExcelFile(null);
        setParsedData([]);
        setImportResults([]);
        setImportErrors([]);
        setShowImportDialog(false);
        setShowResultsDialog(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const downloadFailedRows = () => {
        const failedResults = importResults.filter(result => !result.success);
        
        if (failedResults.length === 0) {
            return;
        }

        // Create Excel data with failed rows
        const headers = ['ID', 'Full Name', 'National ID', 'Quantity (kg)', 'Price', 'Total', 'Order Date', 'Error Reason'];
        const failedData = [
            headers,
            ...failedResults.map(result => [
                result.data.orderNumber || '',
                result.data.fullName || '',
                result.data.nationalId || '',
                result.data.quantity_per_kg || '',
                result.data.price || '',
                result.data.total || '',
                result.data.orderDate ? result.data.orderDate.toISOString().split('T')[0] : '',
                result.error || 'Unknown error'
            ])
        ];

        // Add notes at the bottom
        failedData.push(
            ['', '', '', '', '', '', '', ''],
            ['NOTE: These rows failed to import', '', '', '', '', '', '', ''],
            ['NOTE: Fix the errors and try importing again', '', '', '', '', '', '', ''],
            ['NOTE: Remove the "Error Reason" column before re-importing', '', '', '', '', '', '', '']
        );

        const worksheet = XLSX.utils.aoa_to_sheet(failedData);
        
        // Set column widths
        worksheet['!cols'] = [
            { width: 15 }, // ID
            { width: 20 }, // Full Name
            { width: 15 }, // National ID
            { width: 14 }, // Quantity (kg)
            { width: 12 }, // Price
            { width: 12 }, // Total
            { width: 15 }, // Order Date
            { width: 30 }  // Error Reason
        ];

        // Style the error column header
        if (worksheet['G1']) {
            worksheet['G1'].s = {
                fill: { fgColor: { rgb: "FFFF0000" } },
                font: { color: { rgb: "FFFFFFFF" }, bold: true }
            };
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Failed Rows');
        
        // Generate filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const filename = `order_import_failed_${timestamp}.xlsx`;
        
        XLSX.writeFile(workbook, filename);
    };

    return (
        <Layout>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    py: 8,
                    width: '70%',
                    margin: 'auto',
                    '@media screen and (max-width: 576px)': {width: '90%'}
                }}
            >
                {/* Excel Import Section */}
                <Card sx={{ mb: 3 }}>
                    <CardHeader
                        title="Import Orders from Excel"
                        subheader="Upload an Excel file to bulk import orders"
                    />
                    <CardContent>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<Download />}
                                onClick={downloadTemplate}
                            >
                                Download Template
                            </Button>
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleExcelFileSelect}
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                            />
                            <Button
                                variant="contained"
                                startIcon={<CloudUpload />}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {excelFile ? excelFile.name : 'Upload Excel File'}
                            </Button>
                        </Box>

                        {importErrors.length > 0 && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {importErrors.map((error, index) => (
                                    <div key={index}>{error}</div>
                                ))}
                            </Alert>
                        )}
                    </CardContent>
                </Card>

                <form
                    noValidate
                    onSubmit={handleSubmit}
                >
                    <Card sx={{pb: 2}}>
                        <CardHeader
                            title="Shto Blerjen"
                        />
                        <CardContent sx={{pt: 0}}>
                            <Box sx={{m: -1.5}}>
                                <Grid
                                    container
                                    spacing={3}
                                >
                                    {/*<Grid*/}
                                    {/*    xs={12}*/}
                                    {/*    md={6}*/}
                                    {/*>*/}
                                    {/*    <TextField*/}
                                    {/*        fullWidth*/}
                                    {/*        select*/}
                                    {/*        error={error}*/}
                                    {/*        label="Klienti"*/}
                                    {/*        name="idClient"*/}
                                    {/*        type='text'*/}
                                    {/*        onChange={handleChange}*/}
                                    {/*        value={order?.idClient}*/}
                                    {/*        variant="outlined"*/}
                                    {/*        InputLabelProps={{*/}
                                    {/*            shrink: order?.idClient ? true : false,*/}
                                    {/*        }}*/}
                                    {/*    >*/}
                                    {/*        {orderCreateForm?.clients.map((client: Client | any) => {*/}
                                    {/*            return (*/}
                                    {/*                <MenuItem*/}
                                    {/*                    key={client.idClient}*/}
                                    {/*                    value={client.idClient}*/}
                                    {/*                >*/}
                                    {/*                    {`${client.firstName} ${client.lastName}`}*/}
                                    {/*                </MenuItem >*/}
                                    {/*            )*/}
                                    {/*        })}*/}
                                    {/*    </TextField>*/}
                                    {/*</Grid>*/}
                                    <Grid
                                        xs={12}
                                        md={6}
                                    >
                                        <TextField
                                            fullWidth
                                            error={errors.quantity_per_kg}
                                            label="Sasia (kg)"
                                            name="quantity_per_kg"
                                            onChange={handleChange}
                                            value={order?.quantity_per_kg}
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: order?.quantity_per_kg ? true : false,
                                            }}
                                        />
                                    </Grid>
                                    <Grid
                                        xs={12}
                                        md={6}
                                    >
                                        <TextField
                                            fullWidth
                                            error={errors.price}
                                            label="Çmimi"
                                            name="price"
                                            onChange={handleChange}
                                            value={order?.price}
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: order?.price ? true : false,
                                            }}
                                        />
                                    </Grid>
                                    <Grid
                                        xs={12}
                                        md={6}
                                    >
                                        <FormControl sx={{width: '100% !important'}}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DemoContainer components={['DatePicker']} sx={{width: '100% !important', paddingTop: '0 !important'}}>
                                                    <DatePicker
                                                        label="Data e porosisë"
                                                        value={order?.orderDate}
                                                        onChange={onDateChange}
                                                        disablePast={user?.userType === 'agent' ? true : false}
                                                        format={'DD/MM/YYYY'}
                                                        sx={{width: '100% !important',
                                                            border: errors.orderDate ? '1px solid red' : 'none',
                                                            borderRadius: '10px'}}
                                                    />
                                                </DemoContainer>
                                            </LocalizationProvider>
                                        </FormControl>
                                    </Grid>

                                    <Grid
                                        xs={12}
                                        md={6}
                                    >
                                        <TextField
                                            fullWidth
                                            error={errors.type}
                                            label="Lloji"
                                            name="type"
                                            onChange={handleChange}
                                            value={order?.type}
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: order?.type ? true : false,
                                            }}
                                        />
                                    </Grid>
                                    {/*<Grid*/}
                                    {/*    xs={12}*/}
                                    {/*    md={6}*/}
                                    {/*>*/}
                                    {/*    <TextField*/}
                                    {/*        fullWidth*/}
                                    {/*        error={error}*/}
                                    {/*        label="Materiali"*/}
                                    {/*        name="material"*/}
                                    {/*        onChange={handleChange}*/}
                                    {/*        value={order?.material}*/}
                                    {/*        variant="outlined"*/}
                                    {/*        InputLabelProps={{*/}
                                    {/*            shrink: order?.material ? true : false,*/}
                                    {/*        }}*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}
                                    <Grid
                                        xs={12}
                                        md={6}
                                    >
                                        <TextField
                                            fullWidth
                                            error={errors.totalPrice}
                                            label="Çmimi total"
                                            name="totalPrice"
                                            onChange={handleChange}
                                            value={order?.quantity_per_kg && order?.price ? (+order.quantity_per_kg * +order.price).toFixed(2) : ''}
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: (order?.quantity_per_kg || order?.price || order?.totalPrice) ? true : false,
                                            }}
                                        />
                                    </Grid>
                                    {/*<Grid*/}
                                    {/*    xs={12}*/}
                                    {/*    md={6}*/}
                                    {/*>*/}
                                    {/*    <TextField*/}
                                    {/*        fullWidth*/}
                                    {/*        select*/}
                                    {/*        // error={error}*/}
                                    {/*        label="Përdoruesi"*/}
                                    {/*        name="idUser"*/}
                                    {/*        type='text'*/}
                                    {/*        onChange={handleChange}*/}
                                    {/*        value={order?.idUser?.idUser || ''}*/}
                                    {/*        variant="outlined"*/}
                                    {/*        InputLabelProps={{*/}
                                    {/*            shrink: order?.idUser?.idUser ? true : false,*/}
                                    {/*        }}*/}
                                    {/*    >*/}
                                    {/*        {orderCreateForm?.users*/}
                                    {/*            .filter((userItem: User | any) => userItem.idUser === user?.idUser)*/}
                                    {/*                .map((userItem: User | any) => (*/}
                                    {/*                    <MenuItem key={userItem.idUser} value={userItem.idUser}>*/}
                                    {/*                        {`${userItem.firstName} ${userItem.lastName}`}*/}
                                    {/*                    </MenuItem>*/}
                                    {/*                ))}*/}
                                    {/*    </TextField>*/}
                                    {/*</Grid>*/}
                                </Grid>
                            </Box>
                        </CardContent>
                        <Divider/>
                        <CardActions sx={{justifyContent: 'right', pt: 2}}>
                            <Link to={`/clients/${clientId}`}>
                                <Button variant="contained" sx={{ backgroundColor: theme.palette.grey[500], '&:hover': {backgroundColor: theme.palette.grey[600]}, }}>
                                    Anulo
                                </Button>
                            </Link>
                            <Button variant="contained" type='submit'>
                                Ruaj
                            </Button>
                        </CardActions>
                    </Card>
                </form>

                {/* Import Preview Dialog */}
                <Dialog 
                    open={showImportDialog} 
                    onClose={() => setShowImportDialog(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        Preview Import Data
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Found {parsedData.length} rows to import. Review the data before proceeding.
                        </Typography>
                        
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Row</TableCell>
                                        <TableCell>Order Number</TableCell>
                                        <TableCell>Full Name</TableCell>
                                        <TableCell>National ID</TableCell>
                                        <TableCell>Quantity (kg)</TableCell>
                                        <TableCell>Price</TableCell>
                                        <TableCell>Total</TableCell>
                                        <TableCell>Order Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {parsedData.map((item) => (
                                        <TableRow key={item.rowNumber}>
                                            <TableCell>{item.rowNumber}</TableCell>
                                            <TableCell>{item.orderNumber}</TableCell>
                                            <TableCell>{item.fullName}</TableCell>
                                            <TableCell>{item.nationalId}</TableCell>
                                            <TableCell>{item.quantity_per_kg}</TableCell>
                                            <TableCell>€{item.price?.toFixed(2)}</TableCell>
                                            <TableCell>€{item.total?.toFixed(2)}</TableCell>
                                            <TableCell>{item.orderDate?.toLocaleDateString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowImportDialog(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleExcelImport} 
                            variant="contained"
                            disabled={importing}
                        >
                            {importing ? 'Importing...' : `Import ${parsedData.length} Orders`}
                        </Button>
                    </DialogActions>
                    {importing && (
                        <Box sx={{ px: 3, pb: 3 }}>
                            <LinearProgress />
                            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                                Importing orders...
                            </Typography>
                        </Box>
                    )}
                </Dialog>

                {/* Import Results Dialog */}
                <Dialog 
                    open={showResultsDialog} 
                    onClose={() => setShowResultsDialog(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        Import Results
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mb: 2 }}>
                            <Chip
                                icon={<CheckCircle />}
                                label={`${importResults.filter(r => r.success).length} successful`}
                                color="success"
                                sx={{ mr: 1, mb: 1 }}
                            />
                            <Chip
                                icon={<ErrorIcon />}
                                label={`${importResults.filter(r => !r.success).length} failed`}
                                color="error"
                                sx={{ mr: 1, mb: 1 }}
                            />
                            <Chip
                                label={`${importResults.filter(r => r.clientCreated).length} new clients`}
                                color="info"
                                sx={{ mr: 1, mb: 1 }}
                            />
                            <Chip
                                label={`${importResults.filter(r => r.orderCreated).length} new orders`}
                                color="primary"
                                sx={{ mb: 1 }}
                            />
                        </Box>

                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Row</TableCell>
                                        <TableCell>Order Number</TableCell>
                                        <TableCell>Client</TableCell>
                                        <TableCell>New Client</TableCell>
                                        <TableCell>New Order</TableCell>
                                        <TableCell>Error</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {importResults.map((result, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    icon={result.success ? <CheckCircle /> : <ErrorIcon />}
                                                    label={result.success ? 'OK' : 'Error'}
                                                    color={result.success ? 'success' : 'error'}
                                                />
                                            </TableCell>
                                            <TableCell>{result.row}</TableCell>
                                            <TableCell>{result.data.orderNumber}</TableCell>
                                            <TableCell>{result.data.fullName}</TableCell>
                                            <TableCell>{result.clientCreated ? '✓' : '-'}</TableCell>
                                            <TableCell>{result.orderCreated ? '✓' : '-'}</TableCell>
                                            <TableCell>{result.error || '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'space-between' }}>
                        <Box>
                            {importResults.filter(r => !r.success).length > 0 && (
                                <Button 
                                    startIcon={<Download />}
                                    onClick={downloadFailedRows}
                                    color="error"
                                    variant="outlined"
                                >
                                    Download Failed Rows ({importResults.filter(r => !r.success).length})
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button onClick={() => setShowResultsDialog(false)}>
                                Close
                            </Button>
                            <Button onClick={resetImport} variant="contained">
                                Import Again
                            </Button>
                        </Box>
                    </DialogActions>
                </Dialog>
            </Box>
        </Layout>
    )
}